<script setup lang="ts">
import { Mask } from 'maska';
import type { UserDocument } from '@/types/UserData';

const emit = defineEmits(['reset-anti-robot-token']);

type Props = {
  antiRobotToken: string;
};

const { antiRobotToken } = defineProps<Props>();

const { t } = useTexts();
const { registerData, validationData } = storeToRefs(useRegisterStore());
const { isSocialAuthFlow, socialService } = useAuthSocial();

const { isRegisterRequestDocument, isRegisterRequestBirthDate } = useAuth();

const emailFieldError = ref<string>('');
const emailConfirmFieldError = ref<string>('');
const documentField = ref<string>('');
const isLoadingRegisterPage = ref<boolean>(false);
const isDocumentCPFBirthDateDisabled = ref<boolean>(false);
const updateBirthDateField = ref(0);

const nationalities = [
  { id: 1, value: 'Brasileiro (a)' },
  { id: 2, value: 'Alemão (a)' },
  { id: 3, value: 'Americano (a)' },
  { id: 4, value: 'Argentino (a)' },
  { id: 5, value: 'Canadense (a)' },
  { id: 6, value: 'Chileno (a)' },
  { id: 7, value: 'Espanhol (a)' },
  { id: 8, value: 'Francês (a)' },
  { id: 9, value: 'Indiano (a)' },
  { id: 10, value: 'Italiano (a)' },
  { id: 11, value: 'Japonês (a)' },
  { id: 12, value: 'Mexicano (a)' },
  { id: 13, value: 'Português (a)' },
  { id: 14, value: 'Russo (a)' },
  { id: 15, value: 'Sueco (a)' },
  { id: 16, value: 'Suíço (a)' },
  { id: 17, value: 'Outro' },
];

const unmaskedCpf = computed<string>(() => {
  const mask = new Mask({ mask: '###.###.###-##' });

  return mask.unmasked(documentField.value);
});

const onBlurEmailFields = () => {
  if (!isEmailValid(registerData.value.email)) {
    emailFieldError.value = t('auth.register.error.email_invalid');
    validationData.value.fields.email = false;
  } else if (registerData.value.email !== registerData.value.email_confirm) {
    emailConfirmFieldError.value = t(
      'auth.register.error.email_confirm_invalid',
    );
    validationData.value.fields.email_confirm = false;
  } else {
    validationData.value.fields.email = true;
    validationData.value.fields.email_confirm = true;
  }
};

function handleSelectedDate(formattedDate: any) {
  if (typeof formattedDate !== 'string' || formattedDate === '') {
    registerData.value.birth_date = '';
    validationData.value.fields.birth_date = false;

    return;
  }

  updateSelectedDate(formattedDate);
}

function updateSelectedDate(formattedDate: any) {
  registerData.value.birth_date = formattedDate;
  registerData.value.birth_date
    ? (validationData.value.fields.birth_date = true)
    : (validationData.value.fields.birth_date = false);
}

function onDocumentRemoved() {
  registerData.value.name = '';
  validationData.value.fields.name = false;
  registerData.value.document = '';
  validationData.value.fields.document = false;
  emit('reset-anti-robot-token');
}

function onDocumentValidated(data: UserDocument) {
  if (data.name) {
    registerData.value.name = data.name;
    validationData.value.fields.name = true;
  }
  if (data.birth_date) {
    fillBirthDate(data.birth_date);
  }
  registerData.value.document = unmaskedCpf.value;
  validationData.value.fields.document = true;
}

function fillBirthDate(date: string) {
  registerData.value.birth_date = date;
  updateBirthDateField.value++;
}

function checkNationalities() {
  if (registerData.value.nationalities.length === 0) {
    validationData.value.fields.nationalities = false;

    return;
  }
  validationData.value.fields.nationalities = true;
}

watch(() => registerData.value.nationalities, checkNationalities);

onMounted(() => {
  registerData.value.nationalities = [nationalities[0]];
});
</script>

<template>
  <form class="registerFields">
    <BaseLoading
      class="!bg-auth-bg-primary"
      :icon-loader="true"
      :show="isLoadingRegisterPage"
    />

    <template v-if="isSocialAuthFlow">
      <AuthSocialAuth
        buttons-only
        with-titles
      />
    </template>

    <div :class="$style.emailField">
      <label
        class="registerLabel"
        for="email"
      >
        {{ $t('auth.register.email') }} <span>*</span>
      </label>

      <BaseFormInput
        id="email"
        v-model="registerData.email"
        autocomplete="off"
        :disabled="isSocialAuthFlow"
        :error="emailFieldError"
        :info="isSocialAuthFlow ? '' : $t('multiStepRegister.email_info')"
        :initial-value="registerData.email"
        name="email"
        placeholder="ex: alguem@email.com"
        :required="true"
        type="email"
        @blur="onBlurEmailFields"
        @focus="emailFieldError = ''"
      />

      <div
        v-if="isSocialAuthFlow"
        :class="$style.socialAuthInfo"
      >
        <NuxtIcon
          aria-hidden="true"
          filled
          :name="`social/${socialService}-color`"
        />
      </div>
    </div>

    <template v-if="!isSocialAuthFlow">
      <label
        class="registerLabel"
        for="email_confirm"
      >
        {{ $t('auth.register.email_confirm') }} <span>*</span>
      </label>
      <BaseFormInput
        id="email_confirm"
        v-model="registerData.email_confirm"
        autocomplete="off"
        :error="emailConfirmFieldError"
        :initial-value="registerData.email_confirm"
        name="email_confirm"
        placeholder="ex: alguem@email.com"
        :required="true"
        type="email"
        @blur="onBlurEmailFields"
        @focus="emailConfirmFieldError = ''"
      />
    </template>

    <template v-if="isRegisterRequestDocument">
      <label
        class="registerLabel"
        for="cpf"
      >
        {{ $t('auth.register.cpf') }} <span>*</span>
      </label>
      <BaseFormInputCpf
        id="cpf"
        v-model:masked="documentField"
        :anti-robot-token="antiRobotToken ?? ''"
        hide-icon
        name="cpf"
        placeholder="ex: 123.456.789-00"
        required
        @remove-document="onDocumentRemoved"
        @reset-anti-robot-token="emit('reset-anti-robot-token')"
        @validate-document="onDocumentValidated"
      />
    </template>

    <template v-if="isRegisterRequestDocument || isRegisterRequestBirthDate">
      <label
        class="registerLabel"
        for="birth_date"
      >
        {{ $t('auth.register.birthdate') }} <span>*</span>
      </label>
      <BaseFormSelectBirthdate
        id="birth_date"
        :key="updateBirthDateField"
        v-model="registerData.birth_date"
        :disabled="isDocumentCPFBirthDateDisabled"
        :icon="false"
        :initial-value="registerData.birth_date"
        :name="'birth_date'"
        :required="true"
        :separate-fields="true"
        @keydown.enter.prevent=""
        @update:selected-date="handleSelectedDate"
      />
    </template>
    <template v-else>
      <input
        id="birth_date"
        v-model="registerData.birth_date"
        name="birth_date"
        type="hidden"
      />
    </template>

    <label
      class="registerLabel"
      for="nationalities"
    >
      {{ $t('auth.register.nationalities') }} <span>*</span>
    </label>
    <BaseFormSelect
      id="nationalities"
      v-model="registerData.nationalities"
      class="w-full"
      multiple
      open-up
      :options="nationalities"
    >
    </BaseFormSelect>
  </form>
</template>

<style module lang="scss">
.emailField {
  @apply relative;

  .socialAuthInfo {
    @apply absolute right-0 bottom-0 text-lg flex justify-center items-center w-[46px] sm:w-[50px] h-[46px] sm:h-[50px];
  }
}
</style>
